import { useEffect, useState, useRef } from 'react';

/**
 * useCountUp — animates a number from previous to `value` when
 * the returned ref becomes visible AND every time `value` changes.
 *
 * - Respects prefers-reduced-motion (snaps to final value)
 * - Uses rAF + easeOutQuart for buttery 60fps
 * - Re-animates when value changes (e.g., async data load)
 */
export function useCountUp(value, { duration = 800, decimals = 0 } = {}) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const seenRef = useRef(false);
  const lastValueRef = useRef(0);
  const prefersReduced = typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  // Track viewport visibility so we only animate when actually on-screen.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) seenRef.current = true;
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Re-run the count-up animation whenever `value` changes.
  useEffect(() => {
    const target = Number(value) || 0;
    if (prefersReduced || !seenRef.current) {
      // Off-screen or reduced motion → just snap.
      setDisplay(target);
      lastValueRef.current = target;
      return;
    }
    const start = performance.now();
    const from = lastValueRef.current;
    if (from === target) return;
    let raf = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 4);
      const current = from + (target - from) * eased;
      setDisplay(Number(current.toFixed(decimals)));
      if (t < 1) raf = requestAnimationFrame(tick);
      else lastValueRef.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration, decimals, prefersReduced]);

  return [ref, display];
}
